import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Search, CalendarClock, Bell, Star, Map as MapIcon, Table, ClipboardList, Trophy,
  BookmarkCheck, ChevronDown, Users, Database, CloudUpload, Play, Upload, Link2,
  GitCompare, Clock, ShieldCheck, Zap, FileText, HelpCircle, Smartphone, Medal, Coins, Mail,
} from "lucide-react";

const RED = "#C8000F";
const BLUE = "#003087";

// ---- Quick-start cheat sheet ----
const QUICK = [
  { icon: Table, t: "Browse rodeos", d: "Table, Calendar or Map — all stay in sync." },
  { icon: Search, t: "Search fast", d: "Tap search; your match pins to the top." },
  { icon: Users, t: "Follow riders", d: "My Riders tracks where your competitors are entered & how they place." },
  { icon: CalendarClock, t: "Entry deadlines", d: "The red ticker + Entry Dates show live countdowns." },
  { icon: ClipboardList, t: "Check the draw", d: "Open a rodeo → Draw tab for perf & slack positions." },
  { icon: Trophy, t: "CFR", d: "Live Canadian Finals qualifiers, go-round results & standings." },
  { icon: Coins, t: "Credits & plans", d: "Wallet shows your balance; top up or go Pro anytime." },
  { icon: Smartphone, t: "On your phone", d: "Bottom bar to navigate; tap More for everything else." },
];

// ---- Contestant sections ----
const CONTESTANT = [
  {
    icon: Table, title: "Finding rodeos",
    img: "/guide/find-rodeos.png",
    steps: [
      "Use the sidebar to switch between Table, Calendar and Map — they all stay in sync.",
      "Tap the search icon and type a rodeo or town; the match jumps to the top of the list.",
      "Filters (date range, association, events) narrow the list; your settings are remembered.",
    ],
  },
  {
    icon: FileText, title: "Opening a rodeo — the tabs",
    img: "/guide/entries-tab.png",
    steps: [
      "Tap a rodeo to open it, or use the Info / Draw / Trade buttons right on each row.",
      "Info — location, dates, added money, purse & entry fees, contacts and circuit. Entries — live countdown to entry close with open/close dates.",
      "Draw — your position by performance/slack (uses a credit or your plan). Standings — current world standings for that rodeo's association.",
      "Ground Rules (plus PRCA Listing for PRCA rodeos) show the official rules and listing. Notes lets you jot your own reminders, and Trade shows positions available to trade.",
    ],
  },
  {
    icon: Bell, title: "Entry reminders & alerts",
    img: "/guide/alerts.png",
    steps: [
      "Open Deadlines. Turn on Entry Reminders and choose your windows: 7 days, 3 days, 24 hours, same day.",
      "You'll get in-app reminders before entries close for any rodeo you've starred, picked or entered.",
      "If PRCA changes a deadline or rule on a rodeo you follow, you get a priority alert explaining what changed.",
      "Tap any notification to jump straight to that rodeo's Entries tab.",
    ],
  },
  {
    icon: Star, title: "Watchlist & My Rodeos",
    steps: [
      "On a rodeo page, tap the ★ Watch button to follow it — watched rodeos get higher-priority reminders.",
      "Pick a 1st/2nd performance or mark a rodeo entered; those show up under My Rodeos and also feed your reminders.",
      "Use 'Can I Make It?' and My Season to plan travel and track your year.",
    ],
  },
  {
    icon: ClipboardList, title: "Reading the board — preferences & entries",
    steps: [
      "Each rodeo shows a week strip of its performance days. Tap a day's circle to set your 1st preference (red 1); tap again to make it your 2nd preference (dark 2).",
      "When the rodeo secretary calls back and you know your performance, tap the ✓ on that day to mark yourself entered — the day turns green.",
      "The badges beside the rodeo name are a quick summary of your choices for that rodeo: 1st preference, 2nd preference, and a green check once you're entered.",
      "This is your personal planning board — it does not enter you. Use the Enter button to go to the official entry line.",
    ],
    legend: [
      { badge: "1", bg: "#C8000F", label: "1st preference — the performance you most want to be in." },
      { badge: "2", bg: "#0C1017", border: "#CBD5E1", label: "2nd preference — your backup performance if you don't get your first." },
      { badge: "✓", bg: "#16A34A", label: "Entered — you've marked yourself entered in this rodeo." },
      { badge: "", bg: "#DCFCE7", fg: "#166534", border: "#16A34A", label: "Green day = your performance day — the day you got up after the rodeo's callback." },
      { badge: "+", bg: "#FFFFFF", fg: "#64748B", border: "#CBD5E1", label: "Tap + on a day to set a preference; tap the ✓ to mark (or clear) that day as entered." },
    ],
  },
  {
    icon: Users, title: "Follow your competitors — My Riders",
    steps: [
      "Open My Riders and search any contestant, then tap Follow to track them all season.",
      "Each rider card shows where they're entered, recent results and standings movement; tap Map to see their next stops.",
      "Your plan sets how many you can follow (Rookie 1, Semi Pro 5, Professional 15); extra follows use credits.",
    ],
  },
  {
    icon: Medal, title: "Results, standings, movers & top money",
    steps: [
      "Results shows recent payoffs and placings; Standings has live world and circuit standings.",
      "Movers highlights who's climbing fastest, and Top Money ranks season earnings.",
      "NFR Cutline shows who's in and out of the Top 15, with the money gap to the bubble.",
    ],
  },
  {
    icon: Trophy, title: "Canadian Finals Rodeo (CFR)",
    steps: [
      "The CFR page tracks qualifiers by event, the cut lines and payouts for the Canadian Finals.",
      "Go-Round Results update live from the official cfr.ca results sheet as each round is scored.",
      "Season + CFR $ shows real-time combined standings during finals week.",
    ],
  },
  {
    icon: Coins, title: "Credits, Wallet & plans",
    steps: [
      "Some actions — prepared entries, draw lookups, results & standings audits, and extra follows — use credits.",
      "Your Wallet shows your balance and history; top up with a credit pack or upgrade your plan for included usage.",
      "Semi Pro and Professional include more each month; Professional unlocks unlimited entries, lookups and live audits.",
    ],
  },
  {
    icon: MapIcon, title: "Map view",
    img: "/guide/map.png",
    steps: [
      "The map plots every rodeo; tap a pin to see details and open the rodeo.",
      "Selecting a rodeo in the Table highlights it on the Map and vice-versa — they share one selection.",
    ],
  },
  {
    icon: Smartphone, title: "On your phone (mobile app)",
    gallery: ["/guide/mobile-table.png", "/guide/mobile-map.png", "/guide/mobile-entries.png", "/guide/mobile-alerts.png"],
    steps: [
      "The bottom bar switches Table / Calendar / Map / My Riders. Tap More for everything else — Results, CFR, Entry Dates, Wallet and this Guide.",
      "The red bar at the top is the live entry-closing ticker; tap any rodeo in it to open it.",
      "Scroll down and the top/bottom bars tuck away for a full-screen list; scroll up to bring them back.",
      "Tap a rodeo to open its tabs (Info, Entries, Draw, Standings, Ground Rules, Notes). Tap ★ to watch it and get reminders.",
      "Set your reminder windows under More → Entry Dates / Deadlines.",
    ],
  },
];

// ---- Admin / operator sections ----
const ADMIN = [
  {
    icon: Database, title: "PBJ Data Sources dashboard",
    img: "/guide/admin-pbj.png",
    steps: [
      "Settings → 'Data Sources → PRCA PBJ'. Each source (Entry Closings, Long Listings, Ground Rules) has two independent statuses.",
      "Collector status = can this server scrape? HEALTHY / COLLECTOR ERROR / COLLECTOR OFFLINE / DISABLED.",
      "Data status = do we have usable data? CURRENT · N records · via <origin>. A failed scrape never deletes last-known-good data.",
      "Last Successful and Last Attempt are shown separately, plus a 'View error' button for any failure.",
    ],
  },
  {
    icon: CloudUpload, title: "How data flows to production",
    steps: [
      "pbj.prorodeo.org is a Blazor app with no public feed, so a headless browser must scrape it.",
      "The PREVIEW environment is the collector 'worker' — it runs the browser and pushes results to production's secure ingest endpoint.",
      "Production itself never runs a browser (keeps it light and crash-free), so its collector shows DISABLED by design — that's expected.",
      "This runs automatically twice a day (6am & 6pm Mountain).",
    ],
  },
  {
    icon: Play, title: "Sync to Production & Run Now",
    steps: [
      "On the preview worker, 'Sync to Production' collects all three sources and pushes them to the live app — use it when you want fresh data immediately.",
      "'Run Now' (per source) re-scrapes a single source. It's disabled on servers that can't run the browser (e.g. production).",
      "Zero records returned = treated as a failure; existing data is kept untouched.",
    ],
  },
  {
    icon: Upload, title: "Manual Upload (fallback)",
    steps: [
      "Each source has its own Upload button. Paste CSV/JSON (sample format is shown) to load data by hand.",
      "Uploads run through the same pipeline (versioning, change detection, deadlines, alerts) and are tagged 'Manual upload' as the data origin.",
    ],
  },
  {
    icon: Link2, title: "Unmatched queue",
    steps: [
      "Some entries (often city-only closings like 'Sioux Falls') don't auto-link to a rodeo. They appear in the Unmatched panel.",
      "Search for the correct rodeo and link it — the app remembers the mapping for next time, so it won't need re-linking.",
      "Unlinked items still show in the ticker but won't populate a specific rodeo's Entries tab until linked.",
    ],
  },
  {
    icon: GitCompare, title: "Changes & history",
    steps: [
      "The Changes panel logs every field that changed (old → new), with important changes flagged.",
      "Every version is snapshotted, so you always have an audit trail of what PRCA published and when.",
    ],
  },
  {
    icon: Clock, title: "Automatic schedule",
    steps: [
      "A platform cron fires twice daily (6am & 6pm Mountain) on the preview worker and syncs to production — no manual step needed.",
      "If a scrape is slow or empty, it fails safely and the next run retries; your live data never goes blank.",
    ],
  },
  {
    icon: Mail, title: "Messages & user access",
    steps: [
      "Contact-form submissions land in Admin → Messages; mark them read, reply by email, or delete.",
      "Admin → Users lets you grant Free/Pro, suspend or delete a user. Admins and owner accounts automatically get full, free access.",
      "To remove an admin account, change its role off 'admin' first, then delete — you can recreate it anytime with Add user.",
    ],
  },
];

function Section({ icon: Icon, title, steps, img, gallery, accent, legend }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-[#E4E7EB] bg-white overflow-hidden" data-testid={`guide-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#FAFBFC]">
        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}15`, color: accent }}><Icon className="w-5 h-5" /></span>
        <span className="font-display font-bold text-[#14181F] flex-1">{title}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1">
          <div className={`grid gap-4 ${img ? "md:grid-cols-5" : "grid-cols-1"}`}>
            <ol className={`space-y-2.5 ${img ? "md:col-span-3" : ""}`}>
              {steps.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <span className="shrink-0 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center mt-0.5" style={{ background: accent }}>{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            {img && (
              <div className="md:col-span-2">
                <img src={img} alt={title} loading="lazy" className="w-full rounded-lg border border-[#E4E7EB] shadow-sm" />
              </div>
            )}
          </div>
          {legend && (
            <div className="mt-4 rounded-lg border border-[#E4E7EB] bg-[#FAFBFC] p-3" data-testid="guide-legend">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2.5">Key</div>
              <ul className="space-y-2.5">
                {legend.map((l, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    <span className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[12px] font-extrabold border"
                      style={{ background: l.bg, color: l.fg || "#fff", borderColor: l.border || l.bg }}>{l.badge}</span>
                    <span>{l.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {gallery && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {gallery.map((g, i) => (
                <img key={i} src={g} alt={`${title} ${i + 1}`} loading="lazy"
                  className="h-80 w-auto shrink-0 rounded-[1.25rem] border-4 border-[#14181F] shadow-md bg-[#0D1117]" />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HelpGuide() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("contestant");
  const activeTab = tab === "admin" && !isAdmin ? "contestant" : tab;
  const list = activeTab === "contestant" ? CONTESTANT : ADMIN;
  const accent = activeTab === "contestant" ? RED : BLUE;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="help-guide-page">
      {/* header */}
      <div className="rounded-2xl p-6 sm:p-8 text-white mb-6" style={{ background: `linear-gradient(135deg, ${RED}, #8E0009)` }}>
        <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase tracking-widest mb-2"><HelpCircle className="w-4 h-4" /> Guide & Cheat Sheet</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Get the most out of Rodeo Pilot</h1>
        <p className="text-white/85 mt-2 text-sm sm:text-base max-w-2xl">Everything you need to find rodeos, follow your competitors, never miss an entry deadline, track results and the road to the finals — in one place.</p>
      </div>

      {/* quick start cheat sheet */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3"><Zap className="w-5 h-5" style={{ color: RED }} /><h2 className="font-display text-xl font-bold text-[#14181F]">Quick start</h2></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK.map((q, i) => (
            <div key={i} className="rounded-xl border border-[#E4E7EB] bg-white p-3.5" data-testid={`guide-quick-${i}`}>
              <q.icon className="w-5 h-5 mb-2" style={{ color: RED }} />
              <div className="font-display font-bold text-sm text-[#14181F]">{q.t}</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">{q.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setTab("contestant")} data-testid="guide-tab-contestant"
          className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border transition-colors ${activeTab === "contestant" ? "text-white border-transparent" : "bg-white text-slate-600 border-[#E4E7EB]"}`}
          style={activeTab === "contestant" ? { background: RED } : {}}><Users className="w-4 h-4" /> For Contestants</button>
        {isAdmin && (
          <button onClick={() => setTab("admin")} data-testid="guide-tab-admin"
            className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border transition-colors ${activeTab === "admin" ? "text-white border-transparent" : "bg-white text-slate-600 border-[#E4E7EB]"}`}
            style={activeTab === "admin" ? { background: BLUE } : {}}><ShieldCheck className="w-4 h-4" /> For Admins</button>
        )}
      </div>

      <div className="space-y-3">
        {list.map((s, i) => <Section key={`${activeTab}-${i}`} {...s} accent={accent} />)}
      </div>

      <div className="mt-8 rounded-xl border border-[#E4E7EB] bg-[#FAFBFC] p-5 text-center">
        <p className="text-sm text-slate-600">Ready to go? <Link to="/table" className="font-bold" style={{ color: RED }}>Browse rodeos</Link> or <Link to="/deadlines" className="font-bold" style={{ color: RED }}>set up your entry reminders</Link>.</p>
      </div>
    </div>
  );
}
